import { useEffect, useRef, useState } from "react";
import styles from "./ChatHeader.module.css";
import ArrowLeft from "./icons/ArrowLeft";
import { useChat } from "../contexts/ChatContext";
import { useUser } from "../contexts/UserContext";
import { NavLink } from "react-router-dom";
import Menu3Dots from "./icons/Menu3Dots";
import { AnimatePresence, motion } from "framer-motion";
import config from "../config";

const ChatHeader = () => {
  const {
    toggleMobileSidebar,
    selectedUserId,
    setSelectedUserId,
    setMobileSidebarOpen,
    mobileSidebarOpen,
  } = useChat();
  const {
    usersWithActiveChat,
    setUsersWithActiveChat,
    usersWithChatHistory,
    setUsersWithChatHistory,
    isActiveChat,
    setIsActiveChat,
    getUsersWithActiveChat,
    getUsersWithChatHistory,
  } = useUser();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isClosingChat, setIsClosingChat] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const handleToggleMobileMenu = () =>
    setMobileMenuOpen((prevState) => !prevState);
  const handleCloseChat = async () => {
    const currentUser = selectedUser;
    if (!currentUser?._id || isClosingChat) {
      return;
    }

    setIsClosingChat(true);
    try {
      const response = await fetch(`${config.backendUrl}/users/close-chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
        },
        body: JSON.stringify({ userId: currentUser._id }),
      });

      if (!response.ok) {
        console.error("Failed to close chat");
        return;
      }

      const [updatedActiveUsers, updatedHistoryUsers] = await Promise.all([
        getUsersWithActiveChat(),
        getUsersWithChatHistory(),
      ]);

      setUsersWithActiveChat(updatedActiveUsers);
      setUsersWithChatHistory(updatedHistoryUsers);

      let nextIsActive = isActiveChat;
      let nextSelectedId: string | null = null;

      if (isActiveChat) {
        if (updatedActiveUsers.length > 0) {
          nextSelectedId = updatedActiveUsers[0]._id ?? null;
        } else if (updatedHistoryUsers.length > 0) {
          nextIsActive = false;
          nextSelectedId = updatedHistoryUsers[0]._id ?? null;
        }
      } else {
        if (updatedHistoryUsers.length > 0) {
          nextSelectedId = updatedHistoryUsers[0]._id ?? null;
        } else if (updatedActiveUsers.length > 0) {
          nextIsActive = true;
          nextSelectedId = updatedActiveUsers[0]._id ?? null;
        }
      }

      setIsActiveChat(nextIsActive);
      setSelectedUserId(nextSelectedId ?? null);
    } catch (error) {
      console.error("Error closing chat", error);
    } finally {
      setIsClosingChat(false);
      setMobileSidebarOpen(true);
      setMobileMenuOpen(false);
    }
  };
  useEffect(() => {
    if (!mobileMenuOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target as Node)
      ) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [mobileMenuOpen]);
  const selectedUser = selectedUserId
    ? [...usersWithActiveChat, ...usersWithChatHistory].find(
        (user) => user._id === selectedUserId
      )
    : undefined;
  const displayName =
    selectedUser?.name ||
    selectedUser?.username ||
    selectedUser?.phone ||
    "בחר שיחה";

  const contextLabel = isActiveChat ? "שיחה פעילה" : "היסטוריית שיחות";

  return (
    <div className={styles.headerContainer}>
      <div className={styles.header}>
        {mobileSidebarOpen ? (
          <>
            <div></div>
            <NavLink to="/">
              <div className={styles.arrowLeft} onClick={toggleMobileSidebar}>
                <ArrowLeft />
              </div>
            </NavLink>
          </>
        ) : (
          <>
            <div className={styles.nameAndMenuContainer}>

              <div className={styles.mobileMenu} ref={mobileMenuRef}>
                <div
                  onClick={handleToggleMobileMenu}
                  style={{ width: "25px", height: "25px" }}
                >
                  <Menu3Dots />
                </div>
                <AnimatePresence>
                  {mobileMenuOpen && (
                    <motion.div
                      className={styles.mobileMenuContent}
                      initial={{ opacity: 1, scale: 0.95, y: -6 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: -6 }}
                      transition={{ duration: 0.15, ease: "easeOut" }}
                    >
                      <div
                        className={styles.mobileMenuItem}
                        onClick={handleCloseChat}
                        role="button"
                      >
                      סגירת שיחה
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              <div className={styles.headerTitleContainer}>
                <div className={styles.headerTitle}>{displayName}</div>
                <span className={styles.headerTitleSpan}>{contextLabel}</span>
              </div>
            </div>
            <div className={styles.arrowLeft} onClick={toggleMobileSidebar}>
              <ArrowLeft />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ChatHeader;
